import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { applicantService } from "../../services/applicant.service";



// ─── ADF types ────────────────────────────────────────────────────────────────

interface ADFMark {
  type: string;
  attrs?: Record<string, string | number | boolean | undefined>;
}

export interface ADFNode {
  type: string;
  text?: string;
  marks?: ADFMark[];
  content?: ADFNode[];
  attrs?: Record<string, string | number | boolean | undefined>;
}

// ─── Text marks ───────────────────────────────────────────────────────────────

function applyMarks(
  children: React.ReactNode,
  marks: ADFMark[],
  key: React.Key
): React.ReactNode {
  return marks.reduce<React.ReactNode>((acc, mark) => {
    switch (mark.type) {
      case "strong":
        return <strong key={key}>{acc}</strong>;
      case "em":
        return <em key={key}>{acc}</em>;
      case "underline":
        return (
          <span key={key} style={{ textDecoration: "underline" }}>
            {acc}
          </span>
        );
      case "strike":
        return (
          <span key={key} style={{ textDecoration: "line-through" }}>
            {acc}
          </span>
        );
      case "code":
        return (
          <code
            key={key}
            style={{
              backgroundColor: "#F4F5F7",
              border: "1px solid #DFE1E6",
              borderRadius: "3px",
              padding: "1px 5px",
              fontFamily:
                "SFMono-Medium, SF Mono, Segoe UI Mono, Roboto Mono, Menlo, Courier New, monospace",
              fontSize: "12px",
              color: "#172B4D",
            }}
          >
            {acc}
          </code>
        );
      case "link": {
        const href = String(mark.attrs?.href ?? mark.attrs?.url ?? "#");
        return (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0052CC", textDecoration: "none" }}
            onMouseEnter={(e) =>
              ((e.target as HTMLAnchorElement).style.textDecoration =
                "underline")
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLAnchorElement).style.textDecoration = "none")
            }
          >
            {acc}
          </a>
        );
      }
      case "textColor":
        return (
          <span key={key} style={{ color: String(mark.attrs?.color ?? "") }}>
            {acc}
          </span>
        );
      case "backgroundColor":
        return (
          <span
            key={key}
            style={{ backgroundColor: String(mark.attrs?.color ?? "") }}
          >
            {acc}
          </span>
        );
      case "subsup":
        return mark.attrs?.type === "sub" ? (
          <sub key={key}>{acc}</sub>
        ) : (
          <sup key={key}>{acc}</sup>
        );
      default:
        return acc;
    }
  }, children);
}

// ─── Expand sub-component (needs local state) ─────────────────────────────────

function ExpandNode({
  node,
  renderContent,
}: {
  node: ADFNode;
  renderContent: (nodes: ADFNode[]) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{ border: "1px solid #DFE1E6", borderRadius: "4px", my: 1 }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: "pointer",
          backgroundColor: "#F4F5F7",
          borderRadius: open ? "4px 4px 0 0" : "4px",
          userSelect: "none",
        }}
      >
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: "#6B778C" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: "#6B778C" }} />
        )}
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#172B4D" }}>
          {String(node.attrs?.title ?? "Click to expand")}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ p: 1.5 }}>{renderContent(node.content ?? [])}</Box>
      )}
    </Box>
  );
}

// ─── Ticket card sub-component ────────────────────────────────────────────────

function TicketCard({ url, inline }: { url: string; inline?: boolean }) {
  const match = url.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/i);
  const ticketKey = match ? match[1].toUpperCase() : null;

  if (inline) {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          backgroundColor: "#F4F5F7",
          border: "1px solid #DFE1E6",
          borderRadius: "3px",
          px: 0.75,
          py: 0.25,
          textDecoration: "none",
          color: "#0052CC",
          fontSize: "12px",
          fontWeight: 500,
          "&:hover": { backgroundColor: "#DEEBFF", borderColor: "#0052CC" },
        }}
      >
        {ticketKey ?? url}
        <OpenInNewIcon sx={{ fontSize: 10 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid #DFE1E6",
        borderRadius: "4px",
        p: 1.5,
        my: 1,
        backgroundColor: "#FAFBFC",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 1,
        "&:hover": { backgroundColor: "#F4F5F7", borderColor: "#B3D4FF" },
      }}
      onClick={() => window.open(url, "_blank")}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "4px",
          backgroundColor: "#0052CC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "10px", color: "#fff", fontWeight: 700 }}>
          J
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#0052CC",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ticketKey ?? "Jira Issue"}
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            color: "#6B778C",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {url}
        </Typography>
      </Box>
      <OpenInNewIcon sx={{ fontSize: 14, color: "#6B778C", flexShrink: 0 }} />
    </Box>
  );
}

// ─── Media node ───────────────────────────────────────────────────────────────

function ProxyMediaResolver({
  issueId,
  filename,
  displayName,
}: {
  issueId: string;
  filename: string;
  displayName: string;
}) {
  const [state, setState] = useState<{ blobUrl: string; mimeType: string } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    applicantService
      .getJiraAttachmentBlob(issueId, filename)
      .then((response) => {
        if (cancelled) return;
        const mimeType =
          response.headers?.["content-type"] || response.data.type || "";
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: mimeType });
        objectUrl = URL.createObjectURL(blob);
        setState({ blobUrl: objectUrl, mimeType });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [issueId, filename]);

  if (error) {
    return (
      <Box sx={{ p: 2, color: "#DE350B", fontSize: "13px" }}>
        Failed to load attachment.
      </Box>
    );
  }
  if (!state) {
    return (
      <Box sx={{ p: 2, color: "#6B778C", fontSize: "13px" }}>
        Loading attachment...
      </Box>
    );
  }

  const { blobUrl, mimeType } = state;

  if (mimeType.startsWith("image/")) {
    return (
      <Box
        component="img"
        src={blobUrl}
        alt={displayName}
        sx={{
          maxWidth: "100%",
          maxHeight: "400px",
          borderRadius: "4px",
          border: "1px solid #DFE1E6",
          cursor: "pointer",
          display: "block",
          my: 0.5,
        }}
        onClick={() => window.open(blobUrl, "_blank")}
      />
    );
  }

  if (mimeType === "application/pdf") {
    return (
      <Box 
        sx={{ 
          my: 2, 
          border: "1px solid #DFE1E6", 
          borderRadius: "4px", 
          overflow: "hidden",
          // Fixes container limits so the browser treats it like a desktop page
          maxWidth: "740px", 
          width: "100%",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}
      >
        <Box 
          sx={{ 
            px: 1.5, 
            py: 1, 
            bgcolor: "#F4F5F7", 
            borderBottom: "1px solid #DFE1E6", 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center" 
          }}
        >
          <a 
            href={blobUrl} 
            download={displayName} 
            style={{ 
              fontSize: "12px", 
              color: "#0052CC", 
              textDecoration: "none", 
              flexShrink: 0, 
              whiteSpace: "nowrap",
              fontWeight: 500
            }}
          >
            Download
          </a>
        </Box>
        {/* Enforces true standard document scaling inside the view window */}
        <iframe 
          src={`${blobUrl}#view=FitH`} 
          width="100%" 
          height="840px" 
          title={displayName} 
          style={{ border: "none", display: "block" }} 
        />
      </Box>
    );
  }


  if (mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || mimeType === "application/msword") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1px solid #DFE1E6", borderRadius: "4px", bgcolor: "#F4F5F7", my: 0.5, minWidth: "240px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: "#172B4D" }}>
            Word Document
          </Typography>
          <a href={blobUrl} download={displayName} style={{ textDecoration: "none", color: "#0052CC", fontSize: "13px", fontWeight: 500, marginTop: "4px" }}>
            Download to view
          </a>
        </Box>
      </Box>
    );
  }

  if (
    mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mimeType === "application/vnd.ms-excel"
  ) {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1px solid #DFE1E6", borderRadius: "4px", bgcolor: "#F4F5F7", my: 0.5, minWidth: "240px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: "#172B4D" }}>
            Excel Spreadsheet
          </Typography>
          <a href={blobUrl} download={displayName} style={{ textDecoration: "none", color: "#0052CC", fontSize: "13px", fontWeight: 500, marginTop: "4px" }}>
            Download to view
          </a>
        </Box>
      </Box>
    );
  }

  if (mimeType.startsWith("video/")) {
    return (
      <Box component="video" controls sx={{ maxWidth: "100%", borderRadius: "4px", display: "block", my: 0.5 }}>
        <source src={blobUrl} type={mimeType} />
        Your browser does not support video.
      </Box>
    );
  }

  if (mimeType.startsWith("audio/")) {
    return (
      <Box component="audio" controls  sx={{ 
        width: "100%", 
        maxWidth: "400px",
        display: "block", 
        my: 0.5 
      }}>
        <source src={blobUrl} type={mimeType} />
        Your browser does not support audio.
      </Box>
    );
  }

  // Truly generic file (zip, docx, etc.) — fallback box, but now with a real blob URL.
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1px solid #DFE1E6", borderRadius: "4px", bgcolor: "#F4F5F7", my: 0.5, minWidth: "240px" }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: "#172B4D" }}>
          {displayName}
        </Typography>
        <a href={blobUrl} download={displayName} style={{ textDecoration: "none", color: "#0052CC", fontSize: "13px", fontWeight: 500, marginTop: "4px" }}>
          Download File
        </a>
      </Box>
    </Box>
  );
}

function MediaNode({ node, issueId }: { node: ADFNode; issueId?: string }) {
  const rawUrl = String(node.attrs?.url ?? "");
  const jiraId = String(node.attrs?.id ?? "");

  const displayName = String(
    node.attrs?.filename ?? node.attrs?.alt ?? node.attrs?.name ?? ""
  ) || (jiraId ? `jiraAttachments-${jiraId}` : "attachment");

  const proxyFilename =
    node.attrs?.filename || node.attrs?.alt || node.attrs?.name
      ? displayName
      : jiraId || "";

  if (issueId && proxyFilename && !rawUrl) {
    return (
      <ProxyMediaResolver
        issueId={issueId}
        filename={proxyFilename}
        displayName={displayName}
      />
    );
  }
  return (
    <Box sx={{ p: 1.5, color: "#6B778C", fontSize: "13px" }}>
      {displayName} (attachment unavailable)
    </Box>
  );
}

// ─── Panel styles ─────────────────────────────────────────────────────────────

const PANEL_STYLES: Record<
  string,
  { bg: string; border: string; icon: string }
> = {
  info: { bg: "#E6F3FF", border: "#0065FF", icon: "ℹ" },
  note: { bg: "#EAE6FF", border: "#6554C0", icon: "📝" },
  tip: { bg: "#E3FCEF", border: "#36B37E", icon: "💡" },
  warning: { bg: "#FFFAE6", border: "#FF8B00", icon: "⚠" },
  error: { bg: "#FFEBE6", border: "#DE350B", icon: "✖" },
  success: { bg: "#E3FCEF", border: "#36B37E", icon: "✓" },
};

// ─── Core node renderer ────────────────────────────────────────────────────────

function renderNode(
  node: ADFNode,
  key: React.Key,
  depth = 0,
  issueId?: string
): React.ReactNode {
  switch (node.type) {
    // ── Doc root ────────────────────────────────────────────────────────────
    case "doc":
      return (
        <Box key={key}>
          {(node.content ?? []).map((n, i) => renderNode(n, i, depth, issueId))}
        </Box>
      );

    // ── Paragraph ───────────────────────────────────────────────────────────
    case "paragraph":
      return (
        <Box
          key={key}
          component="p"
          sx={{
            m: 0,
            mb: 1,
            lineHeight: 1.65,
            "&:last-child": { mb: 0 },
          }}
        >
          {node.content?.length
            ? node.content.map((n, i) => renderNode(n, `${key}-${i}`, depth, issueId))
            : <br />}
        </Box>
      );

    // ── Text ────────────────────────────────────────────────────────────────
    case "text": {
      const raw = node.text ?? "";
      if (!node.marks?.length) {
        return <React.Fragment key={key}>{raw}</React.Fragment>;
      }
      return applyMarks(raw, node.marks, key);
    }

    case "taskList":
  return (
    <Box
      key={key}
      component="ul"
      sx={{ pl: 0, my: 0.75, listStyleType: "none", "& li": { mb: 0.5 } }}
    >
      {(node.content ?? []).map((n, i) =>
        renderNode(n, `${key}-${i}`, depth, issueId)
      )}
    </Box>
  );

case "taskItem": {
  const checked = node.attrs?.state === "DONE";
  return (
    <Box
      key={key}
      component="li"
      sx={{ display: "flex", alignItems: "flex-start", gap: 1, lineHeight: 1.6 }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          mt: "3px",
          flexShrink: 0,
          borderRadius: "3px",
          border: checked ? "1px solid #36B37E" : "1px solid #C1C7D0",
          backgroundColor: checked ? "#36B37E" : "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked && (
          <Box component="svg" viewBox="0 0 16 16" sx={{ width: 10, height: 10 }}>
            <path
              d="M3 8.5L6.2 11.5L13 4.5"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          color: checked ? "#6B778C" : "inherit",
          textDecoration: checked ? "line-through" : "none",
        }}
      >
        {(node.content ?? []).map((n, i) =>
          renderNode(n, `${key}-${i}`, depth, issueId)
        )}
      </Box>
    </Box>
  );
}

    // ── Mention ─────────────────────────────────────────────────────────────
    case "mention":
      return (
        <Box
          key={key}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "#DEEBFF",
            color: "#0052CC",
            borderRadius: "4px",
            px: 0.75,
            py: 0.1,
            fontSize: "inherit",
            fontWeight: 500,
            cursor: "default",
            mx: 0.25,
          }}
        >
          {String(node.attrs?.text ?? node.attrs?.displayName ?? "@Unknown")}
        </Box>
      );

    // ── Emoji ────────────────────────────────────────────────────────────────
    case "emoji":
      return (
        <Box
          key={key}
          component="span"
          title={String(node.attrs?.shortName ?? "")}
          sx={{ fontSize: "1.1em", lineHeight: 1 }}
        >
          {String(node.attrs?.text ?? node.attrs?.shortName ?? "")}
        </Box>
      );

    // ── Hard break ───────────────────────────────────────────────────────────
    case "hardBreak":
      return <br key={key} />;

    // ── Headings ─────────────────────────────────────────────────────────────
    case "heading": {
      const level = Number(node.attrs?.level ?? 1);
      const sizeMap: Record<number, string> = {
        1: "19px",
        2: "16px",
        3: "15px",
        4: "13.5px",
        5: "13.5px",
        6: "12px",
      };
      return (
        <Box
          key={key}
          component={`h${Math.min(level, 6)}` as React.ElementType}
          sx={{
            fontSize: sizeMap[level] ?? "13.5px",
            fontWeight: 600,
            mt: level <= 2 ? 2 : 1.5,
            mb: 0.75,
            color: "#172B4D",
            lineHeight: 1.3,
            fontFamily: "Inter",
          }}
        >
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );
    }

    // ── Lists ─────────────────────────────────────────────────────────────────
    case "bulletList":
      return (
        <Box
          key={key}
          component="ul"
          sx={{ pl: 3, my: 0.75, "& li": { mb: 0.25 }, listStyleType: "disc" }}
        >
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    case "orderedList":
      return (
        <Box
          key={key}
          component="ol"
          sx={{
            pl: 3,
            my: 0.75,
            "& li": { mb: 0.25 },
            listStyleType: "decimal",
          }}
        >
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    case "listItem":
      return (
        <Box key={key} component="li" sx={{ lineHeight: 1.6 }}>
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    // ── Code block ────────────────────────────────────────────────────────────
    case "codeBlock": {
      const lang = String(node.attrs?.language ?? "");
      const code = (node.content ?? [])
        .filter((n) => n.type === "text")
        .map((n) => n.text ?? "")
        .join("");
      return (
        <Box
          key={key}
          sx={{
            backgroundColor: "#1E1E1E",
            borderRadius: "4px",
            my: 1.5,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {lang && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: "#2D2D2D",
                borderBottom: "1px solid #3D3D3D",
                fontSize: "11px",
                color: "#858585",
                fontFamily: "Inter",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {lang}
            </Box>
          )}
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              overflowX: "auto",
              fontFamily:
                "SFMono-Medium, SF Mono, Segoe UI Mono, Roboto Mono, Menlo, Courier New, monospace",
              fontSize: "12px",
              lineHeight: 1.6,
              color: "#D4D4D4",
              whiteSpace: "pre",
            }}
          >
            {code}
          </Box>
        </Box>
      );
    }

    // ── Blockquote ────────────────────────────────────────────────────────────
    case "blockquote":
      return (
        <Box
          key={key}
          sx={{
            borderLeft: "3px solid #DFE1E6",
            pl: 1.5,
            my: 1,
            color: "#5E6C84",
            fontStyle: "italic",
          }}
        >
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    // ── Horizontal rule ───────────────────────────────────────────────────────
    case "rule":
      return (
        <Box
          key={key}
          component="hr"
          sx={{ border: "none", borderTop: "1px solid #DFE1E6", my: 2 }}
        />
      );

    // ── Panel ─────────────────────────────────────────────────────────────────
    case "panel": {
      const panelType = String(node.attrs?.panelType ?? "info");
      const ps = PANEL_STYLES[panelType] ?? PANEL_STYLES.info;
      return (
        <Box
          key={key}
          sx={{
            backgroundColor: ps.bg,
            borderLeft: `4px solid ${ps.border}`,
            borderRadius: "0 4px 4px 0",
            p: 1.5,
            my: 1.5,
            display: "flex",
            gap: 1,
          }}
        >
          <Box component="span" sx={{ fontSize: "14px", flexShrink: 0, mt: "1px" }}>
            {ps.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {(node.content ?? []).map((n, i) =>
              renderNode(n, `${key}-${i}`, depth, issueId)
            )}
          </Box>
        </Box>
      );
    }

    // ── Media ─────────────────────────────────────────────────────────────────
    case "mediaSingle":
      return (
        <Box key={key} sx={{ my: 1.5 }}>
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    case "mediaGroup":
      return (
        <Box
          key={key}
          sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1.5 }}
        >
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </Box>
      );

    case "media":
      return <MediaNode key={key} node={node} issueId={issueId} />;

    // ── Table ─────────────────────────────────────────────────────────────────
    case "table":
      return (
        <Box key={key} sx={{ overflowX: "auto", my: 2 }}>
          <Box
            component="table"
            sx={{
              borderCollapse: "collapse",
              width: "100%",
              fontSize: "13px",
              fontFamily: "Inter",
              "& th, & td": {
                border: "1px solid #DFE1E6",
                p: "6px 10px",
                textAlign: "left",
                verticalAlign: "top",
                minWidth: "60px",
              },
              "& th": {
                backgroundColor: "#F4F5F7",
                fontWeight: 600,
                color: "#172B4D",
              },
              "& tr:nth-of-type(even) td": { backgroundColor: "#FAFBFC" },
            }}
          >
            <tbody>
              {(node.content ?? []).map((n, i) =>
                renderNode(n, `${key}-${i}`, depth, issueId)
              )}
            </tbody>
          </Box>
        </Box>
      );

    case "tableRow":
      return (
        <tr key={key}>
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </tr>
      );

    case "tableHeader":
      return (
        <th key={key}>
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </th>
      );

    case "tableCell":
      return (
        <td key={key}>
          {(node.content ?? []).map((n, i) =>
            renderNode(n, `${key}-${i}`, depth, issueId)
          )}
        </td>
      );

    // ── Ticket cards ──────────────────────────────────────────────────────────
    case "inlineCard":
      return (
        <TicketCard key={key} url={String(node.attrs?.url ?? "")} inline />
      );

    case "blockCard":
      return <TicketCard key={key} url={String(node.attrs?.url ?? "")} />;

    // ── Expand ────────────────────────────────────────────────────────────────
    case "expand":
    case "nestedExpand":
      return (
        <ExpandNode
          key={key}
          node={node}
          renderContent={(nodes) =>
            nodes.map((n, i) => renderNode(n, `${key}-exp-${i}`, depth, issueId))
          }
        />
      );

    // ── Fallback ──────────────────────────────────────────────────────────────
    default:
      if (node.content?.length) {
        return (
          <React.Fragment key={key}>
            {node.content.map((n, i) =>
              renderNode(n, `${key}-${i}`, depth, issueId)
            )}
          </React.Fragment>
        );
      }
      if (node.text) {
        return <React.Fragment key={key}>{node.text}</React.Fragment>;
      }
      return null;
  }
}

// ─── Public component ─────────────────────────────────────────────────────────

interface ADFRendererProps {
  /** Raw commentBody string — either ADF JSON or plain text */
  content: string;
  /** Jira issue key for resolving media UUID attachments via proxy */
  issueId?: string;
}

const ADFRenderer: React.FC<ADFRendererProps> = ({ content, issueId }) => {
  if (!content) return null;

  try {
    const adf: ADFNode = JSON.parse(content);
    if (adf && typeof adf === "object" && (adf.type === "doc" || adf.content)) {
      return (
        <Box
          sx={{
            fontSize: "13.5px",
            color: "#333",
            lineHeight: 1.65,
            fontFamily: "Inter",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            "& p": { margin: 0, marginBottom: "8px" },
            "& p:last-child": { marginBottom: 0 },
            "& ul, & ol": { marginTop: "4px", marginBottom: "4px" },
            "& h1, & h2, & h3, & h4, & h5, & h6": { fontFamily: "Inter" },
          }}
        >
          {renderNode(adf, "root", 0, issueId)}
        </Box>
      );
    }
  } catch {
    // Not valid JSON — fall through to plain text
  }

  return (
    <Typography
      sx={{
        fontFamily: "Inter",
        fontSize: "13.5px",
        color: "#333",
        lineHeight: 1.65,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {content}
    </Typography>
  );
};

export default ADFRenderer;
